/**
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corp. 2020, 2023
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../lib/@waca/core-client/js/core-client/utils/UniqueId',
	'./api/AffinityServiceAPI'
], (APIFactory, UniqueId, AffinityServiceAPI) => {

	/** Endpoint URL for query context related request */
	const REQUEST_URL = 'v1/datasets/querycontext';
	/** Content type of requests */
	const CONTENT_TYPE = 'application/json';

	class AffinityService {

		/**
		 * @classdesc Class to handle affinity requests, and manage the affinity context.
		 * @constructs
		 * @public
		 */
		constructor({ features }) {
			this.dashboard = features['API'];
			this.logger = features['Logger'];
			this.ajaxSvc = this.dashboard.getGlassCoreSvc('.Ajax');
			const featureChecker = this.dashboard.getGlassCoreSvc('.FeatureChecker');
			const disabled = featureChecker && featureChecker.checkValue('dashboard', 'affinity', 'disabled');
			this._routingId = null;
			this.createAffinityPromise = disabled ?  Promise.resolve() : this.createAffinityContext();
		}

		destroy() {
			// Delete affinity context will cancel all query requests associated with this context.
			return this.deleteAffinityContext().finally(() => {
				this.dashboard = null;
				this.ajaxSvc = null;
				this.createAffinityPromise = null;
				this.affinityContext = null;
			});
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [AffinityServiceAPI]);
			}
			return this.api;
		}

		getAffinityContext() {
			return this.createAffinityPromise;
		}

		resetAffinity(affinityInfo = null) {
			let resetRequired = false;
			// reset required if this._routingId is null, otherwise we already get context with routing for the dashboard
			if (affinityInfo && affinityInfo.routingInfo && affinityInfo.routingInfo.length && !this._routingId) {
				this._routingId = affinityInfo.routingInfo;
				resetRequired = true;
			}
			if (!this.affinityContext.expired) {
				resetRequired = true;
				this.affinityContext.expired = true;
			}
			if (resetRequired) {
				this.createAffinityPromise = this.createAffinityContext();
			}
		}

		createAffinityContext() {
			const request = {
				type: 'post',
				url: REQUEST_URL,
				contentType: CONTENT_TYPE
			};
			if (this._routingId) {
				request.headers =  { [AffinityServiceAPI.AFFINITY_CONTEXT.ROUTING_ID]: this._routingId };
			}

			return this.ajaxSvc.ajax(
				request
			).then(response => {
				if (response && response.data && response.jqXHR) {
					this.affinityContext = {
						[AffinityServiceAPI.AFFINITY_CONTEXT.AFFINITY_ID]: response.jqXHR.getResponseHeader(AffinityServiceAPI.AFFINITY_CONTEXT.AFFINITY_ID),
						[AffinityServiceAPI.AFFINITY_CONTEXT.CONTEXT_ID]: response.data.queryContextId
					};
					if (this._routingId) {
						this.affinityContext[AffinityServiceAPI.AFFINITY_CONTEXT.ROUTING_ID] = this._routingId;
					}
					return this.affinityContext;
				} else {
					this.affinityContext = {};
					this.logger.error('Missing info from queryContext request');
					// TODO Don't reject until implement cancel?
					return Promise.resolve();
				}
			}).catch(error => {
				this.affinityContext = {};
				this.logger.error(error);
				return Promise.reject(error);
			});
		}

		deleteAffinityContext() {
			if (this.affinityContext && this.affinityContext[AffinityServiceAPI.AFFINITY_CONTEXT.CONTEXT_ID]) {
				const headers = {
					[AffinityServiceAPI.AFFINITY_CONTEXT.AFFINITY_ID]: this.affinityContext[AffinityServiceAPI.AFFINITY_CONTEXT.AFFINITY_ID]
				};
				return this.ajaxSvc.ajax({
					type: 'delete',
					url: REQUEST_URL + '/' + this.affinityContext[AffinityServiceAPI.AFFINITY_CONTEXT.CONTEXT_ID],
					contentType: CONTENT_TYPE,
					headers
				});
			}
			return Promise.resolve();
		}

		generateRequestID() {
			return UniqueId.get();
		}
	}

	return AffinityService;
});
